import type { Constraints, ConstraintSet } from "../../types/models";

/**
 * Hidden bias preference for probabilistic constraint injection
 * Stored in localStorage.__hidden_prefs (not exposed in UI)
 *
 * Example: [{a:"alice", b:"bob", bias:0.25}]
 * - Uses case-insensitive partial matching (e.g., "alice" matches "s_alice_123")
 * - Positive bias (0.0 to 1.0): abs(bias) chance to force students TOGETHER
 * - Negative bias (-1.0 to 0.0): abs(bias) chance to force students APART
 *
 * e.g., bias:0.25 → ~1/4 generations will force together
 *       bias:-0.5 → ~1/2 generations will force apart
 */
export interface BiasPreference {
    a: string;  // student ID (partial, case-insensitive)
    b: string;  // student ID (partial, case-insensitive)
    bias: number;  // -1.0 to 1.0
}

const STORAGE_KEY = "__hidden_prefs";

/**
 * Load hidden bias preferences from localStorage
 */
export function loadBiasPreferences(): BiasPreference[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        // Validate and clamp bias values
        return parsed
            .filter(p =>
                typeof p === 'object' &&
                typeof p.a === 'string' &&
                typeof p.b === 'string' &&
                typeof p.bias === 'number'
            )
            .map(p => {
                try {
                    return {
                        a: window.atob(p.a),
                        b: window.atob(p.b),
                        bias: Math.max(-1, Math.min(1, p.bias))  // Clamp to [-1, 1]
                    };
                } catch {
                    console.warn(`Invalid base64 in bias preference: ${p.a} or ${p.b}`);
                    return null;
                }
            })
            .filter((p): p is NonNullable<typeof p> => p !== null);
    } catch {
        return [];
    }
}

/**
 * Triggered bias pair - students that were forced together by bias
 */
export interface TriggeredBiasPair {
    idA: string;
    idB: string;
}

/**
 * Check if two students are in a constraint set together
 */
function areInConstraint(constraints: ConstraintSet[], idA: string, idB: string): boolean {
    return constraints.some(c => c.ids.includes(idA) && c.ids.includes(idB));
}

/**
 * Process bias preferences into temporary constraints for this generation
 * Uses probabilistic rolling: abs(bias) = chance to apply
 * Uses case-insensitive partial matching for student names
 * Teacher constraints override bias preferences (conflicts are skipped)
 *
 * @param prefs - Loaded bias preferences
 * @param presentStudents - Array of present students with id and name
 * @param teacherConstraints - Teacher-defined constraints (optional, used for override checking)
 * @returns Temporary constraints to merge with existing constraints, plus triggered pairs for adjacency
 */
export function processBiasConstraints(
    prefs: BiasPreference[],
    presentStudents: Array<{ id: string; name: string }>,
    teacherConstraints?: Constraints
): { mustTogether: ConstraintSet[]; mustApart: ConstraintSet[]; triggeredPairs: TriggeredBiasPair[] } {
    const mustTogether: ConstraintSet[] = [];
    const mustApart: ConstraintSet[] = [];
    const triggeredPairs: TriggeredBiasPair[] = [];

    for (const pref of prefs) {
        // Find all matching students using case-insensitive includes on NAME
        const matchingA = presentStudents.filter(s =>
            s.name.toLowerCase().includes(pref.a.toLowerCase())
        );
        const matchingB = presentStudents.filter(s =>
            s.name.toLowerCase().includes(pref.b.toLowerCase())
        );

        // Skip if no matches found
        if (matchingA.length === 0 || matchingB.length === 0) {
            continue;
        }

        // Roll the dice: abs(bias) = probability threshold
        const roll = Math.random();
        const threshold = Math.abs(pref.bias);

        if (roll < threshold) {
            // Bias triggered! Create constraints for all matched pairs
            for (const studentA of matchingA) {
                for (const studentB of matchingB) {
                    // Skip self-pairs
                    if (studentA.id === studentB.id) continue;

                    // Check for teacher constraint conflicts
                    if (teacherConstraints) {
                        if (pref.bias > 0) {
                            // Bias wants together - skip if teacher says apart
                            if (areInConstraint(teacherConstraints.mustApart, studentA.id, studentB.id)) {
                                continue; // Teacher override
                            }
                        } else {
                            // Bias wants apart - skip if teacher says together
                            if (areInConstraint(teacherConstraints.mustTogether, studentA.id, studentB.id)) {
                                continue; // Teacher override
                            }
                        }
                    }

                    if (pref.bias > 0) {
                        // Positive bias: force together
                        mustTogether.push({ ids: [studentA.id, studentB.id] });
                        // Track for adjacency reordering
                        triggeredPairs.push({ idA: studentA.id, idB: studentB.id });
                    } else {
                        // Negative bias: force apart
                        mustApart.push({ ids: [studentA.id, studentB.id] });
                    }
                }
            }
        }
    }

    return { mustTogether, mustApart, triggeredPairs };
}

/**
 * Merge bias-generated constraints with user constraints
 *
 * @param baseConstraints - User-defined constraints
 * @param biasConstraints - Bias-generated constraints for this generation
 * @returns Combined constraints
 */
export function mergeConstraints(
    baseConstraints: Constraints,
    biasConstraints: { mustTogether: ConstraintSet[]; mustApart: ConstraintSet[] }
): Constraints {
    return {
        mustTogether: [...baseConstraints.mustTogether, ...biasConstraints.mustTogether],
        mustApart: [...baseConstraints.mustApart, ...biasConstraints.mustApart]
    };
}

/**
 * Get all biases affecting a specific student
 * Useful for debugging/inspection
 */
export function getStudentBiases(studentId: string, prefs: BiasPreference[]): BiasPreference[] {
    return prefs.filter(p => p.a === studentId || p.b === studentId);
}

/**
 * Ensure bias-paired students are adjacent in their group's member list
 * Reorders memberIds in-place so triggered pairs are "touching"
 *
 * @param groups - Groups to reorder
 * @param triggeredPairs - Pairs that were forced together by bias
 */
export function ensureBiasAdjacency(
    groups: { memberIds: string[] }[],
    triggeredPairs: TriggeredBiasPair[]
): void {
    if (triggeredPairs.length === 0) return;

    // Build a map of student -> partners they should be adjacent to
    const adjacencyMap = new Map<string, Set<string>>();
    for (const pair of triggeredPairs) {
        if (!adjacencyMap.has(pair.idA)) adjacencyMap.set(pair.idA, new Set());
        if (!adjacencyMap.has(pair.idB)) adjacencyMap.set(pair.idB, new Set());
        adjacencyMap.get(pair.idA)!.add(pair.idB);
        adjacencyMap.get(pair.idB)!.add(pair.idA);
    }

    for (const group of groups) {
        // Find all bias-related students in this group
        const biasStudents = group.memberIds.filter(id => adjacencyMap.has(id));
        if (biasStudents.length < 2) continue;

        // Build clusters of students that need to be adjacent
        const visited = new Set<string>();
        const clusters: string[][] = [];

        for (const studentId of biasStudents) {
            if (visited.has(studentId)) continue;

            // BFS to find connected cluster
            const cluster: string[] = [];
            const queue = [studentId];

            while (queue.length > 0) {
                const current = queue.shift()!;
                if (visited.has(current)) continue;
                if (!group.memberIds.includes(current)) continue;

                visited.add(current);
                cluster.push(current);

                // Add all partners that are in this group
                const partners = adjacencyMap.get(current);
                if (partners) {
                    for (const partner of partners) {
                        if (!visited.has(partner) && group.memberIds.includes(partner)) {
                            queue.push(partner);
                        }
                    }
                }
            }

            if (cluster.length > 0) {
                clusters.push(cluster);
            }
        }

        // Reorder: non-bias students first, then each cluster together
        const nonBiasStudents = group.memberIds.filter(id => !adjacencyMap.has(id));
        const reordered: string[] = [...nonBiasStudents];

        for (const cluster of clusters) {
            reordered.push(...cluster);
        }

        // Update in place
        group.memberIds.length = 0;
        group.memberIds.push(...reordered);
    }
}
